/** A record's dock with a preview (the tour plan §1 "Dock", D6): **Preview | Changes N**
 *  in one column.
 *
 *  It is the Ledger with a segment switch (`tabs`) and, under Preview, the record's preview
 *  (the phone) in place of the changes (`body`): the ledger's header, its Save foot and
 *  ⌘S / ⌘Z / Esc stay mounted under both segments. From 1100 px of content it is present in
 *  view mode too, showing Preview (`Ledger mode="view"` — the same element, so pressing E
 *  moves nothing); edit mode switches it to Changes once the draft's first change exists.
 *  Below 1100 px the ledger is the bottom sheet (edit mode only) and Preview is the
 *  header's drawer (`PreviewDrawer`).
 *
 *  A tab without a preview (the tour's History) passes none: in edit mode it docks the
 *  plain ledger, as the booking record's non-record tabs do. */
import { Box, Skeleton, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { useDraft, useDraftSummary } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { DockSegments, useDockSegment } from '@/features/records/ledger/DockSegments';
import { Ledger, type LedgerVariant } from '@/features/records/ledger/Ledger';

/** A record's dock with a preview (O5; the tour's D6): the phone needs 380 px — the plain
 *  ledger column (`LEDGER_WIDTH`) is 340. Passed to `LedgerDock` as its `width`; the
 *  narrow layout's drawer is as wide. */
export const PREVIEW_DOCK_WIDTH = 380;

export interface PreviewDockProps {
  variant: LedgerVariant;
  editing: boolean;
  /** May edit this record: without it there is no switch at all (Changes is never
   *  theirs); the Ledger's title "Preview" labels the dock. */
  canEdit: boolean;
  /** What Preview shows (the record's preview pane); null: no preview on this tab. */
  preview: ReactNode | null;
}

export function PreviewDock({ variant, editing, canEdit, preview }: PreviewDockProps) {
  const { count } = useDraftSummary();
  const { receipt } = useRecordEditor();
  const { restored, state } = useDraft();
  const discarded = state.undoStack[state.undoStack.length - 1]?.kind === 'discard';
  const { segment, choose } = useDockSegment({ editing, count, receipt, restored: Boolean(restored) && count > 0, discarded });
  // The bottom sheet is the ledger alone (Preview is the header's drawer there); a tab
  // without a preview docks the plain ledger.
  if (variant === 'sheet' || preview === null) return <Ledger variant={variant} />;
  return (
    <Ledger
      variant={variant}
      mode={editing ? 'edit' : 'view'}
      viewTitle="Preview"
      {...(canEdit ? { tabs: <DockSegments segment={segment} onChange={choose} editing={editing} count={count} /> } : {})}
      {...(segment === 'preview' ? { body: preview } : {})}
    />
  );
}

/** The dock while the record loads, on the loaded dock's chrome (the ledger's side column in
 *  view mode): its header — the Preview title and the edit buttons' 30 px room — over a
 *  hairline, the Preview | Changes switch's row for an operator who may edit (`segments`),
 *  then the pane's first rows: the status and Render, the two-line note, the pickers' row. */
export function PreviewDockSkeleton({ segments }: { segments: boolean }) {
  return (
    <Box
      data-preview-dock-skeleton
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', overflow: 'hidden' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 30,
          pl: 1.75,
          pr: 0.75,
          py: 0.75,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" component="div">
          <Skeleton width={64} />
        </Typography>
      </Box>
      {segments && (
        <Box sx={{ px: 1.75, pt: 1 }} data-preview-dock-skeleton-segments>
          <Skeleton variant="rounded" height={34} />
        </Box>
      )}
      <Box sx={{ px: 1.75, py: 1.25, display: 'grid', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 32 }}>
          <Skeleton width="50%" />
          <Box sx={{ flex: 1 }} />
          <Skeleton variant="rounded" width={88} height={32} />
        </Box>
        <Typography variant="caption" component="div" sx={{ minHeight: '3.32em' }}>
          <Skeleton />
          <Skeleton width="70%" />
        </Typography>
        <Skeleton variant="rounded" height={40} />
      </Box>
    </Box>
  );
}
